import {
  GatewayDeclarationActivite,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { parseAnswerValue } from "./answerValue";

type QuestionDef = GatewayQuestionnaire.QuestionDefinitionResponse;
type OptionColor = GatewayQuestionnaire.OptionColor;

const { ActiviteIobsp } = GatewayDeclarationActivite;
type ActiviteIobsp = GatewayDeclarationActivite.ActiviteIobsp;

// ---------------------------------------------------------------------------
// Capacité professionnelle IOBSP
// ---------------------------------------------------------------------------

const NIVEAU_1_IOBSP_LABEL = "Niveau 1 IOBSP";
const NIVEAU_2_IOBSP_LABEL = "Niveau 2 IOBSP";

const IOBSP_NIVEAU_DEPENDENT_JUSTIFICATIF_LABELS = new Set([
  "BTS 313, 314, 128 ou 122",
  "Stage pro niv.2 80h",
  "Stage pro niv.2 74h (RAC)",
  "Stage pro niv.2 50h (Immo)",
  "Stage pro niv.2 42h (Conso)",
  "Stage pro niv.2 36h (Services de paiement)",
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 40h",
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 36h (RAC)",
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 24h (Immo)",
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 18h (Conso)",
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 16h (Services de paiement)",
]);

/**
 * Justificatifs liés à une diversification d'activité : en plus du niveau 2,
 * ils ne sont verts que si au moins une des activités listées figure dans la
 * section « Diversification de l'activité » de la déclaration d'activité IOBSP
 * (activité principale ou activités ≥ 10 % du CA). Les justificatifs absents
 * de cette table ne dépendent que du niveau.
 */
const IOBSP_JUSTIFICATIF_REQUIRED_ACTIVITES: Record<string, ActiviteIobsp[]> = {
  "Stage pro niv.2 74h (RAC)": [ActiviteIobsp.REGROUPEMENTS_DE_CREDIT],
  "Stage pro niv.2 50h (Immo)": [
    ActiviteIobsp.CREDIT_IMMOBILIER,
    ActiviteIobsp.PRET_VIAGER_HYPOTHECAIRE,
  ],
  "Stage pro niv.2 42h (Conso)": [ActiviteIobsp.CREDIT_A_LA_CONSOMMATION],
  "Stage pro niv.2 36h (Services de paiement)": [
    ActiviteIobsp.SERVICES_DE_PAIEMENT,
  ],
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 36h (RAC)": [
    ActiviteIobsp.REGROUPEMENTS_DE_CREDIT,
  ],
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 24h (Immo)": [
    ActiviteIobsp.CREDIT_IMMOBILIER,
    ActiviteIobsp.PRET_VIAGER_HYPOTHECAIRE,
  ],
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 18h (Conso)": [
    ActiviteIobsp.CREDIT_A_LA_CONSOMMATION,
  ],
  "Exp pro ≥ 1 an IOBSP niveau 3 + formation complémentaire 16h (Services de paiement)":
    [ActiviteIobsp.SERVICES_DE_PAIEMENT],
};

// ---------------------------------------------------------------------------
// Capacité professionnelle IAS
// ---------------------------------------------------------------------------

const NIVEAU_1_IAS_LABEL = "Niveau 1 IAS";
const NIVEAU_2_IAS_LABEL = "Niveau 2 IAS";
const NIVEAU_3_IAS_LABEL = "Niveau 3 IAS";

const IAS_NIVEAU_LABELS = [
  NIVEAU_1_IAS_LABEL,
  NIVEAU_2_IAS_LABEL,
  NIVEAU_3_IAS_LABEL,
];

/**
 * Pour la question « Quelle est la nature du justificatif transmis ? », les
 * options ci-dessous sont vertes lorsque le niveau requis (question du niveau
 * de capacité professionnelle) fait partie des niveaux listés, et rouges dans
 * le cas contraire. Les options absentes de cette table gardent leur couleur
 * d'origine.
 */
const IAS_JUSTIFICATIF_GREEN_NIVEAUX: Record<string, string[]> = {
  "BTS 313": [NIVEAU_2_IAS_LABEL, NIVEAU_3_IAS_LABEL],
  "Stage pro niv.2 150h": [NIVEAU_2_IAS_LABEL, NIVEAU_3_IAS_LABEL],
  "Formation niveau 3 durée raisonnable": [NIVEAU_3_IAS_LABEL],
  "Exp pro ≥ 1 an cadre": [NIVEAU_2_IAS_LABEL, NIVEAU_3_IAS_LABEL],
  "Exp pro ≥ 2 ans non cadre": [NIVEAU_2_IAS_LABEL, NIVEAU_3_IAS_LABEL],
  "Exp pro ≥ 6 mois": [NIVEAU_3_IAS_LABEL],
};

// ---------------------------------------------------------------------------

/**
 * Retourne le label de l'option sélectionnée à la question du niveau de
 * capacité professionnelle, identifiée par la présence de tous les libellés de
 * niveau fournis. Retourne undefined si la question est absente ou sans réponse.
 */
function findSelectedNiveauLabel(
  questions: QuestionDef[],
  answers: Record<string, string>,
  niveauLabels: string[]
): string | undefined {
  const niveauQuestion = questions.find((q) =>
    niveauLabels.every((label) => q.options.some((o) => o.label === label))
  );
  if (!niveauQuestion) return undefined;

  const selectedIds = parseAnswerValue(answers[niveauQuestion.id]);
  const selected = niveauQuestion.options.find((o) =>
    selectedIds.includes(o.id)
  );
  return selected?.label;
}

function computeIobspOverrides(
  question: QuestionDef,
  questions: QuestionDef[],
  answers: Record<string, string>,
  declaredIobspActivites?: ActiviteIobsp[]
): Record<string, OptionColor> {
  const targetOptions = question.options.filter((o) =>
    IOBSP_NIVEAU_DEPENDENT_JUSTIFICATIF_LABELS.has(o.label)
  );
  if (targetOptions.length === 0) return {};

  const niveauLabel = findSelectedNiveauLabel(questions, answers, [
    NIVEAU_1_IOBSP_LABEL,
    NIVEAU_2_IOBSP_LABEL,
  ]);
  let niveauColor: OptionColor | undefined;
  if (niveauLabel === NIVEAU_2_IOBSP_LABEL) niveauColor = "green";
  else if (niveauLabel === NIVEAU_1_IOBSP_LABEL) niveauColor = "red";
  if (!niveauColor) return {};

  // Sans donnée de déclaration d'activité (chargement, erreur), on retombe sur
  // la coloration par niveau seul plutôt que de marquer des rouges à tort.
  return Object.fromEntries(
    targetOptions.map((option) => {
      const requiredActivites =
        IOBSP_JUSTIFICATIF_REQUIRED_ACTIVITES[option.label];
      const matchesDiversification =
        !requiredActivites ||
        !declaredIobspActivites ||
        requiredActivites.some((activite) =>
          declaredIobspActivites.includes(activite)
        );
      return [option.id, matchesDiversification ? niveauColor : "red"];
    })
  );
}

function computeIasOverrides(
  question: QuestionDef,
  questions: QuestionDef[],
  answers: Record<string, string>
): Record<string, OptionColor> {
  const niveauLabel = findSelectedNiveauLabel(
    questions,
    answers,
    IAS_NIVEAU_LABELS
  );
  if (!niveauLabel || !IAS_NIVEAU_LABELS.includes(niveauLabel)) return {};

  const overrides: Record<string, OptionColor> = {};
  question.options.forEach((option) => {
    const greenNiveaux = IAS_JUSTIFICATIF_GREEN_NIVEAUX[option.label];
    if (!greenNiveaux) return;
    overrides[option.id] = greenNiveaux.includes(niveauLabel) ? "green" : "red";
  });
  return overrides;
}

export function computeOptionColorOverrides(
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined,
  question: QuestionDef,
  questions: QuestionDef[],
  answers: Record<string, string>,
  declaredIobspActivites?: ActiviteIobsp[]
): Record<string, OptionColor> {
  if (slug === GatewayQuestionnaire.QuestionnaireSlug.cq_capacite_pro_iobsp)
    return computeIobspOverrides(
      question,
      questions,
      answers,
      declaredIobspActivites
    );
  if (slug === GatewayQuestionnaire.QuestionnaireSlug.cq_capacite_pro_ias)
    return computeIasOverrides(question, questions, answers);
  return {};
}
